import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";

interface ProgressChartProps {
  dailyValues: Array<{
    date: string;
    value: number;
    goal_met: boolean;
  }>;
  challengeType: string;
}

export function ProgressChart({ dailyValues, challengeType }: ProgressChartProps) {
  if (!dailyValues || dailyValues.length === 0) {
    return null;
  }

  const sortedData = [...dailyValues].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const maxValue = Math.max(...sortedData.map(d => d.value), 10);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-accent" />
          Progress Over Time
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-end justify-between gap-1 h-48">
            {sortedData.slice(-14).map((day, index) => {
              const height = (day.value / maxValue) * 100;
              const date = new Date(day.date);
              const dayLabel = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center gap-1">
                  <div className="flex-1 flex items-end w-full">
                    <div 
                      className={`w-full rounded-t transition-all ${
                        day.goal_met 
                          ? 'bg-secondary' 
                          : 'bg-muted'
                      }`}
                      style={{ height: `${height}%`, minHeight: '4px' }}
                      title={`${dayLabel}: ${day.value}`}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground rotate-45 origin-left whitespace-nowrap">
                    {date.getDate()}
                  </span>
                </div>
              );
            })}
          </div>
          
          <div className="flex items-center justify-center gap-4 pt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-secondary" />
              <span className="text-xs text-muted-foreground">Goal Met</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-muted" />
              <span className="text-xs text-muted-foreground">Incomplete</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
